import {
  dashboardv99990101Client,
  type Dashboardv99990101Api
} from '@sequencehq/api/dashboard/v99990101'
import { fetchQuery } from '@sequencehq/api'
import { useMutation } from '@sequencehq/api/utils'
import { useNotifications } from '@sequencehq/core-components'
import type { CountriesAlpha2 } from '@sequencehq/api/commonEnums'
import { logError } from '@sequencehq/utils'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { SUPPORTED_COUNTRIES_WITH_STATES } from '../taxRates.constants'
import type { TaxCategory, TaxRate } from '../taxRates.types'

type CreateTaxRateBody = Dashboardv99990101Api.PostTaxRate.CreateTaxRateBody

export type CreateTaxRateInput = {
  country: CountriesAlpha2 | undefined
  taxCategories: Record<TaxCategory['id'], TaxRate['amount']>
  state?: string
}

export function useCreateTaxRate(
  props: { invalidateQueries?: boolean } = { invalidateQueries: true }
) {
  const { displayNotification } = useNotifications()

  const {
    mutateAsync: postTaxRate,
    isError,
    reset: resetPostTaxRate
  } = useMutation(dashboardv99990101Client.postTaxRate, {
    invalidateQueries: props.invalidateQueries ? undefined : () => []
  })

  return async (newTaxRateInput: CreateTaxRateInput) => {
    const country = newTaxRateInput.country

    if (!country) {
      return
    }

    // Ensure we have the most up to date version of the data when we save,
    // rather than rely on an on-mount useQuery (which could drift if the modal
    // is left open for a while).
    const taxCategoriesData = await fetchQuery(
      apiQueryClient,
      dashboardv99990101Client.getTaxCategories
    )
    const taxCategories = taxCategoriesData?.items ?? []

    const taxRatesToCreate: { body: CreateTaxRateBody }[] = Object.entries(
      newTaxRateInput.taxCategories
    ).map(([categoryId, amount]) => {
      const taxCategoryName = taxCategories.find(
        taxCategory => taxCategory.id === categoryId
      )?.name

      const selectedState = SUPPORTED_COUNTRIES_WITH_STATES.includes(country)
        ? newTaxRateInput.state
        : undefined

      const generatedTaxRateName = [country, selectedState, taxCategoryName]
        .filter(Boolean)
        .join(' ')

      return {
        body: {
          name: generatedTaxRateName,
          invoiceName: generatedTaxRateName,
          invoiceShortName: generatedTaxRateName,
          isPublished: true,
          taxCategoryId: categoryId,
          region: selectedState,
          amount,
          country
        }
      }
    })

    await Promise.all(
      taxRatesToCreate.map(taxRateToCreate => {
        return postTaxRate(taxRateToCreate, {
          onError: err => {
            logError(err)
          }
        })
      })
    )

    if (isError) {
      displayNotification('Failed to create tax rates', {
        type: 'error'
      })
      return
    }

    displayNotification('Tax rates created', {
      type: 'success'
    })

    resetPostTaxRate()
  }
}
